import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/seo/SEOHead';
import Breadcrumbs, { getBreadcrumbSchema } from '@/components/seo/Breadcrumbs';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SERVICES } from '@/data/servicesData';
import { Button } from '@/components/ui/button';
import { Printer, ArrowRight, Search, CheckCircle2, ShieldCheck, Zap } from 'lucide-react';
import { 
  createCollectionPageSchema, 
  createBreadcrumbListSchema, 
  createSpeakable 
} from '@/utils/schemaGenerator';

export default function ServicesOverviewPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Document Printing', 'Binding & Finishing', 'Specialty Printing', 'Same Day Express'];

  const filteredServices = SERVICES.filter(svc => {
    const matchesSearch = svc.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          svc.keywords.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCat = selectedCategory === 'All' || svc.category === selectedCategory;
    return matchesSearch && matchesCat;
  });

  const breadcrumbItems = [
    { label: 'Services', url: '/printing-services' }
  ];

  const collectionSchema = createCollectionPageSchema({
    name: "Online Printing & Xerox Services Catalog",
    description: "Browse 25+ online printing services including PDF printing, online xerox, thesis binding, and photo printing.",
    url: "https://print.inchpaper.com/printing-services",
    hasPart: SERVICES.map(svc => ({ title: svc.title, url: `/${svc.slug}` }))
  });

  const breadcrumbSchema = createBreadcrumbListSchema([
    { name: "Home", url: "/" },
    { name: "Printing Services", url: "/printing-services" }
  ]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-between antialiased">
      <SEOHead 
        title="Printing Services | Inchpaper Self-Service Cloud Print Kiosks"
        description="Explore 25+ online printing services: PDF printing, online xerox, color copies, thesis hardbound, spiral binding, and glossy photo prints with 10-second instant kiosk pickup."
        keywords="printing services, online xerox, print pdf online, document printing india, thesis printing, spiral binding, photo print"
        canonical="https://print.inchpaper.com/printing-services"
        schemas={[
          collectionSchema, 
          breadcrumbSchema,
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Printing Services Catalog",
            "speakable": createSpeakable(["h1", "p"])
          }
        ]}
      />

      <Navbar />

      <main className="grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Page Header */}
        <div className="mb-10 text-center max-w-3xl mx-auto space-y-3">
          <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
            Our Printing Services
          </h1>
          <p className="text-slate-600 text-sm sm:text-base">
            Upload your files online, select print specifications, pay securely via UPI, and receive high-speed laser prints from Inchpaper self-service kiosks.
          </p>
        </div>

        {/* Search & Filter Bar */}
        <div className="bg-white p-4 sm:p-6 rounded-3xl border border-slate-200/90 shadow-xs mb-8 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
              <input 
                type="text"
                placeholder="Search services (e.g. PDF, Xerox, Thesis, Binding, Photos)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-2xl border border-slate-200 text-sm focus:outline-hidden focus:ring-2 focus:ring-[#7d0909]"
              />
            </div>
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 scrollbar-none">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                    selectedCategory === cat 
                      ? 'bg-[#7d0909] text-white shadow-xs' 
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredServices.map((svc) => (
            <div 
              key={svc.slug}
              className="bg-white p-6 rounded-3xl border border-slate-200/90 shadow-xs hover:shadow-md transition-all flex flex-col justify-between group"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-red-700 bg-red-50 px-2.5 py-1 rounded-full">
                    {svc.category}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-slate-900 group-hover:text-[#7d0909] transition-colors leading-snug">
                  {svc.h1}
                </h3>

                <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                  {svc.tagline}
                </p>

                <div className="space-y-1.5 pt-2">
                  {svc.features.slice(0, 2).map((feat, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 text-xs text-slate-700">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      <span className="truncate">{feat.title}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 mt-4 border-t border-slate-100 flex items-center justify-between gap-2">
                <Link 
                  to={`/${svc.slug}`}
                  className="text-xs font-bold text-[#7d0909] hover:underline inline-flex items-center gap-1"
                >
                  <span>Read Details</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>

                <Link to="/#upload">
                  <Button size="sm" className="bg-[#7d0909] hover:bg-red-800 text-white rounded-xl text-xs font-bold px-3">
                    <Printer className="w-3.5 h-3.5 mr-1" />
                    Print Now
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>

      </main>

      <Footer />
    </div>
  );
}
